// client/src/components/pages/LexiStar/Notifications/NotificationsInbox.tsx (ST-00251)
//
// The LexiStar notifications inbox. Route /lexistar/notifications, sidebar LexiStar · Operate →
// Notifications (with the unread badge), and the bell's "See all notifications". Page permission:
// the bell's — lexistar.trips.view or lexistar.notifications.view (config/pagePermissions).
//
//   List      the signed-in user's own in-app notifications, newest first (GET
//             /api/lexistar/notifications): severity, title, text, what it is about (truck,
//             driver, trip, route, site — derived by the server, inbox.ts), the time (relative,
//             the SAST date and time on hover / focus), read state, and Open (the deep link,
//             which marks it read). "Load more" pages back with the keyset cursor.
//   Live      `lexistar:notification` (the socket the bell shares) asks the server for what is
//             newer than the top row under the current filters: at the top of the list the new
//             rows slide in highlighted; scrolled down they wait behind a "N new — show" pill,
//             so the reading position never jumps. Announced politely; critical ones assertively.
//   Filters   severity (counts per level), type, rule, truck, driver, route, site, read state,
//             date range (SAST days), free text — all in the URL; each choice shows its count
//             under the other filters (facets). Clear all.
//   Mark read selected rows, or everything the filters match (server-side: {all, filter}).
//   Phone     cards; the filters in one bottom sheet (PhoneFilterSheet) behind a "Show" field,
//             the search and the dates above the cards.
//   Words     ST-00280: types read in the shared label catalogue's words (utils/eventTypeLabels),
//             and an older notification whose title still carries the code is worded.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FaBell, FaCheck, FaChevronRight, FaSearch, FaSyncAlt, FaTimes, FaArrowUp } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import PhoneFilterSheet, { type PhoneChoiceGroup } from '../../../common/PhoneFilterSheet';
import { usePhoneLayout } from '../../../common/phoneLayout';
import { SegmentedControl, ThemedSelect } from '../../../../types/commonComponents';
import { formatMiningDateTime, formatRelativeTime, toApiDateString } from '../../../../utils/dateUtils';
import { withEventLabels } from '../../../../utils/eventTypeLabels';
import { listInbox, markInboxRead } from '../../../../services/lexistarInbox';
import type {
  InboxFacets,
  InboxNotification,
  InboxSeverity,
} from '../../../../types/lexistar-notification-inbox';
import {
  publishMarked,
  subscribeMarked,
  subscribeNotifications,
  unreadStore,
} from '../../../common/lexistarNotificationStream';
import { PageHeader, ErrorBanner, LoadingState, EmptyState, cardClasses } from '../StatusBoard/atoms';
import {
  DIMENSIONS,
  DIMENSION_META,
  SEVERITY_META,
  SEVERITY_ORDER,
  activeFilterCount,
  apiFilters,
  filtersFromParams,
  inAppPath,
  mergeNewest,
  optionLabel,
  paramsFromFilters,
  relatedOf,
  severityOf,
  typeLabel,
  type Dimension,
  type InboxFilters,
} from './inboxModel';

export const PAGE_SIZE = 50;
const SOURCE = 'inbox';
const FRESH_MS = 8000;
const LIVE_DEBOUNCE_MS = 400;

type Unavailable = 'missing' | 'forbidden' | null;

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

const errorText = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { error?: string; message?: string } } } | null)?.response?.data;
  return data?.error || data?.message || fallback;
};

const parseDay = (s: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ── Small pieces ─────────────────────────────────────────────────────────────

function SeverityTag({ severity }: { severity: string | null | undefined }) {
  const meta = SEVERITY_META[severityOf(severity)];
  return (
    <span className={`inline-flex items-center px-1.5 py-px rounded text-[10px] font-bold uppercase tracking-wide ${meta.chip}`}>
      {meta.label}
    </span>
  );
}

/** Relative time; the SAST date and time on hover and keyboard focus (and for screen readers). */
function When({ iso }: { iso: string }) {
  const absolute = `${formatMiningDateTime(iso)} SAST`;
  const relative = formatRelativeTime(iso);
  return (
    <span className="relative inline-flex group/when">
      <time
        dateTime={iso}
        tabIndex={0}
        aria-label={`${relative}, ${absolute}`}
        className="text-xs text-slate-500 whitespace-nowrap tabular-nums rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        {relative}
      </time>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute right-0 top-full mt-1 z-20 hidden group-hover/when:block group-focus-within/when:block whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-[11px] font-medium text-slate-50 shadow-lg"
      >
        {absolute}
      </span>
    </span>
  );
}

// ── The page ─────────────────────────────────────────────────────────────────

export default function NotificationsInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const filterKey = useMemo(() => JSON.stringify(paramsFromFilters(filters)), [filters]);
  const phone = usePhoneLayout();

  const [rows, setRows] = useState<InboxNotification[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [facets, setFacets] = useState<InboxFacets | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState<Unavailable>(null);
  const [pending, setPending] = useState<InboxNotification[]>([]);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [announce, setAnnounce] = useState('');
  const [alert, setAlert] = useState('');
  const [search, setSearch] = useState(filters.q);
  const [, setTick] = useState(0);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const requestRef = useRef(0);
  const atTopRef = useRef(true);
  const topRef = useRef<HTMLDivElement | null>(null);
  const listHeadingRef = useRef<HTMLHeadingElement | null>(null);
  const labelCache = useRef<Record<Dimension, Record<string, string>>>({
    type: {},
    rule: {},
    truck: {},
    driver: {},
    route: {},
    site: {},
  });

  // ── URL ──
  const setFilters = useCallback(
    (patch: Partial<InboxFilters>) => {
      const next = paramsFromFilters({ ...filtersRef.current, ...patch });
      setSearchParams(next, { replace: true });
    },
    [setSearchParams]
  );

  // The search box writes the URL after a pause, and follows it when something else changes it
  // (back / forward, Clear all) — never while the user is still typing what it wrote.
  const wroteQ = useRef(filters.q);
  useEffect(() => {
    if (filters.q !== wroteQ.current) {
      wroteQ.current = filters.q;
      setSearch(filters.q);
    }
  }, [filters.q]);
  useEffect(() => {
    const q = search.trim();
    if (q === filters.q) return undefined;
    const id = setTimeout(() => {
      wroteQ.current = q;
      setFilters({ q });
    }, 350);
    return () => clearTimeout(id);
  }, [search, filters.q, setFilters]);

  // Relative times move on.
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const rememberLabels = (f: InboxFacets | null | undefined) => {
    if (!f) return;
    for (const d of DIMENSIONS) {
      for (const o of f[DIMENSION_META[d].facet]) labelCache.current[d][o.value] = optionLabel(d, o);
    }
  };

  const failed = (err: unknown) => {
    const s = statusOf(err);
    if (s === 404 || s === 503) setUnavailable('missing');
    else if (s === 403) setUnavailable('forbidden');
    else setError(errorText(err, 'The notifications could not be loaded. Try again.'));
  };

  // ── Load (page 1 with facets) whenever the filters change ──
  const load = useCallback(async () => {
    const id = ++requestRef.current;
    setLoading(true);
    setError(null);
    try {
      const res = await listInbox({ ...apiFilters(filtersRef.current), limit: PAGE_SIZE, facets: true });
      if (id !== requestRef.current) return;
      setRows(res.notifications || []);
      setNextCursor(res.next_cursor ?? null);
      setFacets(res.facets ?? null);
      rememberLabels(res.facets);
      setPending([]);
      setSelected(new Set());
      setUnavailable(null);
      if (typeof res.unread === 'number') unreadStore.set(res.unread);
    } catch (err) {
      if (id === requestRef.current) failed(err);
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
    // filterKey stands for the filters (read through the ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    load();
  }, [load]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    const id = requestRef.current;
    try {
      const res = await listInbox({ ...apiFilters(filters), limit: PAGE_SIZE, cursor: nextCursor });
      if (id !== requestRef.current) return;
      setRows((prev) => mergeNewest(prev, res.notifications || []));
      setNextCursor(res.next_cursor ?? null);
      setAnnounce(`${plural((res.notifications || []).length, 'older notification')} loaded`);
    } catch (err) {
      setError(errorText(err, 'Older notifications could not be loaded. Try again.'));
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Live arrivals ──
  useEffect(() => {
    const el = topRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) atTopRef.current = e.isIntersecting;
    });
    io.observe(el);
    return () => io.disconnect();
  }, [unavailable, loading, phone]);

  const markFresh = (ids: string[]) => {
    if (!ids.length) return;
    setFresh((prev) => new Set([...prev, ...ids]));
    setTimeout(() => {
      setFresh((prev) => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }, FRESH_MS);
  };

  const fetchNewer = useCallback(async () => {
    const id = requestRef.current;
    const newest = pendingRef.current[0]?.cursor || rowsRef.current[0]?.cursor;
    try {
      const res = await listInbox({
        ...apiFilters(filtersRef.current),
        limit: PAGE_SIZE,
        facets: true,
        ...(newest ? { after: newest } : {}),
      });
      if (id !== requestRef.current) return;
      if (res.facets) {
        setFacets(res.facets);
        rememberLabels(res.facets);
      }
      const known = new Set([...rowsRef.current, ...pendingRef.current].map((n) => n.id));
      const incoming = (res.notifications || []).filter((n) => !known.has(n.id));
      if (!incoming.length) return;
      if (atTopRef.current && pendingRef.current.length === 0) {
        setRows((prev) => mergeNewest(incoming, prev));
        markFresh(incoming.map((n) => n.id));
        setAnnounce(`${plural(incoming.length, 'new notification')} at the top of the list`);
      } else {
        setPending((prev) => mergeNewest(incoming, prev));
        setAnnounce(`${plural(incoming.length, 'new notification')}. Use Show new to see them.`);
      }
    } catch {
      /* the next arrival or a reload catches up */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const off = subscribeNotifications((n) => {
      if (n.severity === 'C') setAlert(`Critical: ${withEventLabels(n.title) || 'notification'}`);
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        fetchNewer();
      }, LIVE_DEBOUNCE_MS);
    });
    return () => {
      off();
      if (timer) clearTimeout(timer);
    };
  }, [fetchNewer]);

  const showPending = () => {
    const ids = pendingRef.current.map((n) => n.id);
    setRows((prev) => mergeNewest(pendingRef.current, prev));
    setPending([]);
    markFresh(ids);
    topRef.current?.scrollIntoView?.({ block: 'start' });
    listHeadingRef.current?.focus();
  };

  // ── Read state ──
  const applyRead = useCallback((ids: string[] | 'all') => {
    const now = new Date().toISOString();
    const hit = (n: InboxNotification) => ids === 'all' || ids.includes(n.id);
    setRows((prev) => prev.map((n) => (hit(n) && !n.read_at ? { ...n, read_at: now } : n)));
    setPending((prev) => prev.map((n) => (hit(n) && !n.read_at ? { ...n, read_at: now } : n)));
  }, []);

  /** The counts again after a mark-read (the rows already show it). */
  const refreshFacets = useCallback(async () => {
    try {
      const res = await listInbox({ ...apiFilters(filtersRef.current), limit: 1, facets: true });
      if (res.facets) setFacets(res.facets);
      if (typeof res.unread === 'number') unreadStore.set(res.unread);
    } catch {
      /* counts catch up on the next load */
    }
  }, []);

  // The bell marked some (or all) read: show it here too.
  useEffect(
    () =>
      subscribeMarked((e, source) => {
        if (source === SOURCE) return;
        applyRead('all' in e ? 'all' : e.ids);
        refreshFacets();
      }),
    [applyRead, refreshFacets]
  );

  const markIds = async (ids: string[]) => {
    const unreadIds = ids.filter((id) => rowsRef.current.some((n) => n.id === id && !n.read_at));
    if (!unreadIds.length) return;
    applyRead(unreadIds);
    unreadStore.add(-unreadIds.length);
    publishMarked({ ids: unreadIds }, SOURCE);
    try {
      const res = await markInboxRead({ ids: unreadIds });
      if (typeof res?.unread === 'number') unreadStore.set(res.unread);
      refreshFacets();
    } catch (err) {
      setError(errorText(err, 'Could not mark the notifications read. Try again.'));
      load();
    }
  };

  const onOpen = (n: InboxNotification) => {
    if (!n.read_at) markIds([n.id]);
  };

  const filtered = activeFilterCount(filters) > 0;
  const unreadMatching = facets ? facets.status.unread : rows.filter((n) => !n.read_at).length;

  const markAll = async () => {
    setBusy(true);
    try {
      const res = filtered
        ? await markInboxRead({ all: true, filter: apiFilters(filters) })
        : await markInboxRead({ all: true });
      applyRead(filtered ? res.ids || [] : 'all');
      publishMarked(filtered ? { ids: res.ids || [] } : { all: true }, SOURCE);
      if (typeof res?.unread === 'number') unreadStore.set(res.unread);
      setSelected(new Set());
      setAnnounce(`${plural(res?.marked ?? 0, 'notification')} marked read`);
      refreshFacets();
    } catch (err) {
      setError(errorText(err, 'Could not mark the notifications read. Try again.'));
    } finally {
      setBusy(false);
    }
  };

  const markSelected = async () => {
    const ids = [...selected];
    setSelected(new Set());
    await markIds(ids);
    setAnnounce(`${plural(ids.length, 'notification')} marked read`);
  };

  const toggleSelected = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const allSelected = rows.length > 0 && rows.every((n) => selected.has(n.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((n) => n.id)));

  // ── Filter choices ──
  const optionsFor = (d: Dimension) => {
    const list = facets ? facets[DIMENSION_META[d].facet] : [];
    const opts = list.map((o) => ({ value: o.value, label: optionLabel(d, o), count: o.count }));
    const current = filters[d];
    if (current && !opts.some((o) => o.value === current)) {
      opts.unshift({ value: current, label: labelCache.current[d][current] || optionLabel(d, { value: current, label: null }), count: 0 });
    }
    return opts;
  };

  const toggleSeverity = (s: InboxSeverity) =>
    setFilters({
      severity: filters.severity.includes(s) ? filters.severity.filter((x) => x !== s) : [...filters.severity, s],
    });

  const clearAll = () => {
    wroteQ.current = '';
    setSearch('');
    setSearchParams({}, { replace: true });
  };

  const statusOptions = [
    { value: '', label: 'All', badge: facets ? facets.status.unread + facets.status.read : undefined },
    { value: 'unread', label: 'Unread', badge: facets ? facets.status.unread : undefined },
    { value: 'read', label: 'Read', badge: facets ? facets.status.read : undefined },
  ];

  const dateField = (
    <div className="w-full sm:w-64">
      <span className="block text-xs font-medium text-slate-600 mb-1">Received</span>
      {filters.from || filters.to ? (
        <div className="flex items-center gap-1">
          <div className="flex-1 min-w-0">
            <UnifiedDateRangePicker
              startDate={parseDay(filters.from) || parseDay(filters.to) || new Date()}
              endDate={parseDay(filters.to) || new Date()}
              onStartDateChange={(d) => setFilters({ from: d ? toApiDateString(d) : '' })}
              onEndDateChange={(d) => setFilters({ to: d ? toApiDateString(d) : '' })}
              bounds="past"
            />
          </div>
          <button
            type="button"
            onClick={() => setFilters({ from: '', to: '' })}
            aria-label="Any time"
            className="touch-target p-2 rounded-md text-slate-500 hover:bg-slate-200"
          >
            <FaTimes size={11} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() =>
            setFilters({ from: toApiDateString(new Date(Date.now() - 6 * 86400000)), to: toApiDateString(new Date()) })
          }
          className="touch-target w-full text-left px-2 py-1.5 border border-slate-300 rounded-md text-sm text-slate-500 bg-surface hover:border-slate-400"
        >
          Any time — filter by date
        </button>
      )}
    </div>
  );

  const searchField = (
    <div className={phone ? 'relative' : 'relative w-full sm:w-72'}>
      {!phone && (
        <label htmlFor="inbox-search" className="block text-xs font-medium text-slate-600 mb-1">
          Search
        </label>
      )}
      <span className="relative block">
        <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} aria-hidden="true" />
        <input
          id="inbox-search"
          type="search"
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          placeholder="Title, truck, driver, trip, site..."
          aria-label="Search notifications"
          enterKeyHint="search"
          maxLength={200}
          className={`w-full pl-9 pr-3 border border-slate-300 rounded-lg bg-surface focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${phone ? 'h-11 text-base rounded-xl' : 'h-9 text-sm'}`}
        />
      </span>
    </div>
  );

  const severityChips = (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Severity">
      {SEVERITY_ORDER.map((s) => {
        const on = filters.severity.includes(s);
        const count = facets ? facets.severity[s] : null;
        return (
          <button
            key={s}
            type="button"
            aria-pressed={on}
            onClick={() => toggleSeverity(s)}
            className={`touch-target inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              on ? 'border-primary-500 bg-primary-50 text-slate-900 font-semibold' : 'border-slate-300 bg-surface text-slate-700 hover:border-slate-400'
            }`}
          >
            <span className={`inline-block w-2 h-2 rounded-full ${SEVERITY_META[s].dot}`} aria-hidden="true" />
            {SEVERITY_META[s].label}
            {count != null && <span className="tabular-nums text-xs text-slate-500">{count}</span>}
          </button>
        );
      })}
    </div>
  );

  const selects = DIMENSIONS.map((d) => {
    const meta = DIMENSION_META[d];
    const opts = optionsFor(d);
    return (
      <div key={d} className="w-full sm:w-52">
        <label htmlFor={`inbox-${d}`} className="block text-xs font-medium text-slate-600 mb-1">
          {meta.label}
        </label>
        <ThemedSelect
          id={`inbox-${d}`}
          value={filters[d]}
          onChange={(e) => setFilters({ [d]: String(e.target.value) } as Partial<InboxFilters>)}
          aria-label={meta.label}
        >
          <option value="">{meta.all}</option>
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {`${o.label} (${o.count})`}
            </option>
          ))}
        </ThemedSelect>
      </div>
    );
  });

  // Phone: every choice in one sheet.
  const phoneGroups: PhoneChoiceGroup[] = [
    {
      id: 'status',
      legend: 'Show',
      value: filters.status,
      defaultValue: '',
      choices: statusOptions.map((o) => ({ value: o.value, label: o.label, count: o.badge ?? null })),
    },
    {
      id: 'severity',
      legend: 'Severity',
      value: filters.severity.join(','),
      defaultValue: '',
      choices: [
        { value: '', label: 'Every severity', count: facets ? facets.total : null },
        ...(filters.severity.length > 1
          ? [{ value: filters.severity.join(','), label: filters.severity.map((s) => SEVERITY_META[s].label).join(' + ') }]
          : []),
        ...SEVERITY_ORDER.map((s) => ({
          value: s,
          label: SEVERITY_META[s].label,
          swatch: <span className={`inline-block w-2.5 h-2.5 rounded-full ${SEVERITY_META[s].dot}`} aria-hidden="true" />,
          count: facets ? facets.severity[s] : null,
        })),
      ],
    },
    ...DIMENSIONS.filter((d) => optionsFor(d).length > 0).map((d) => ({
      id: d,
      legend: DIMENSION_META[d].label,
      value: filters[d],
      defaultValue: '',
      choices: [
        { value: '', label: DIMENSION_META[d].all },
        ...optionsFor(d).map((o) => ({ value: o.value, label: o.label, count: o.count })),
      ],
    })),
  ];

  const phoneSummary = [
    filters.status ? (filters.status === 'unread' ? 'Unread' : 'Read') : 'All',
    ...filters.severity.map((s) => SEVERITY_META[s].label),
    ...DIMENSIONS.filter((d) => filters[d]).map((d) => labelCache.current[d][filters[d]] || DIMENSION_META[d].label),
  ].join(' · ');

  const phoneFilter = (
    <PhoneFilterSheet
      title="Filter notifications"
      label="Show"
      value={phoneSummary}
      activeCount={activeFilterCount({ ...filters, q: '', from: '', to: '' })}
      groups={phoneGroups}
      onApply={(v) =>
        setFilters({
          status: v.status === 'unread' || v.status === 'read' ? v.status : '',
          severity: (v.severity || '').split(',').filter((s): s is InboxSeverity => (SEVERITY_ORDER as string[]).includes(s)),
          ...Object.fromEntries(DIMENSIONS.map((d) => [d, v[d] ?? filters[d]])),
        })
      }
    />
  );

  const header = (
    <PageHeader
      icon={FaBell}
      title="Notifications"
      subtitle="Every LexiStar alert sent to you, newest first."
      toolbar={
        phone ? null : (
          <SegmentedControl<string>
            options={statusOptions}
            value={filters.status}
            onChange={(v) => setFilters({ status: v === 'unread' || v === 'read' ? v : '' })}
            ariaLabel="Read state"
          />
        )
      }
      actions={
        <button
          type="button"
          onClick={() => load()}
          aria-label="Refresh"
          className={`${phone ? 'touch-target justify-center ' : ''}inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50`}
        >
          <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} />
        </button>
      }
    />
  );

  const liveRegions = (
    <>
      <p className="sr-only" aria-live="polite" role="status">
        {announce}
      </p>
      <p className="sr-only" aria-live="assertive" role="alert">
        {alert}
      </p>
    </>
  );

  if (unavailable) {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-6">
          <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg" role="alert">
            <p className="font-medium">
              {unavailable === 'missing'
                ? 'LexiStar notifications are not installed on this server yet.'
                : 'You do not have access to LexiStar notifications.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const total = facets?.total ?? rows.length;
  const selectedCount = [...selected].filter((id) => rows.some((n) => n.id === id)).length;

  const row = (n: InboxNotification): ReactNode => {
    const unread = !n.read_at;
    const path = inAppPath(n.link);
    const about = relatedOf(n);
    const type = typeLabel(n.event_type);
    // ST-00280: a notification written before the template variables were worded still
    // carries the code in its title (TRIP.ARRIVED_SOURCE: …): word it here.
    const title = withEventLabels(n.title) || type.name;
    const body = withEventLabels(n.body);
    const isFresh = fresh.has(n.id);
    return (
      <li
        key={n.id}
        data-testid="inbox-row"
        data-unread={unread ? 'true' : 'false'}
        className={`relative flex gap-3 px-3 sm:px-4 py-3 transition-colors duration-1000 ${
          phone ? cardClasses : ''
        } ${isFresh ? 'bg-primary-50' : 'bg-surface'}`}
      >
        <span className="flex flex-col items-center gap-2 pt-0.5">
          <input
            type="checkbox"
            className="w-4 h-4 accent-primary-600 [@media(pointer:coarse)]:w-5 [@media(pointer:coarse)]:h-5"
            checked={selected.has(n.id)}
            onChange={() => toggleSelected(n.id)}
            aria-label={`Select ${title}`}
          />
          <span
            className={`inline-block w-2 h-2 rounded-full ${unread ? 'bg-primary-500' : 'bg-transparent'}`}
            aria-hidden="true"
          />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <SeverityTag severity={n.severity} />
            <span className="text-xs font-medium text-slate-500">
              {type.category ? `${type.category} · ` : ''}
              {type.name}
              {n.escalation_tier ? ` · escalation ${n.escalation_tier}` : ''}
            </span>
            {isFresh && (
              <span className="text-[10px] font-bold uppercase tracking-wide text-primary-700">New</span>
            )}
            <span className="ml-auto">
              <When iso={n.created_at} />
            </span>
          </div>
          <p className={`mt-1 text-sm break-words ${unread ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
            {title}
            {unread && <span className="sr-only"> (unread)</span>}
          </p>
          {body && <p className="mt-0.5 text-sm text-slate-600 break-words line-clamp-3">{body}</p>}
          {(about.length > 0 || n.rule_name) && (
            <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="About">
              {about.map((a) => (
                <li key={a.key}>
                  {a.filter ? (
                    <button
                      type="button"
                      onClick={() => setFilters({ [a.filter![0]]: a.filter![1] } as Partial<InboxFilters>)}
                      className="group/chip touch-target inline-flex items-center rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      aria-label={`${a.label} ${a.text}. Show only this ${a.label.toLowerCase()}`}
                      title={`Show only this ${a.label.toLowerCase()}`}
                    >
                      {/* the 44 px tap area on a phone is the button; the chip stays small */}
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-700 group-hover/chip:bg-slate-200">
                        <span className="text-slate-500">{a.label}</span>
                        <span className="font-medium">{a.text}</span>
                      </span>
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-xs text-slate-700">
                      <span className="text-slate-500">{a.label}</span>
                      <span className="font-medium">{a.text}</span>
                    </span>
                  )}
                </li>
              ))}
              {n.rule_name && (
                <li>
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs text-slate-500">
                    Rule <span className="font-medium text-slate-600">{n.rule_name}</span>
                  </span>
                </li>
              )}
            </ul>
          )}
        </div>
        <div className="flex items-start">
          {path ? (
            <Link
              to={path}
              onClick={() => onOpen(n)}
              className="touch-target inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-surface text-sm font-medium text-primary-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label={`Open: ${title}`}
            >
              Open <FaChevronRight size={10} aria-hidden="true" />
            </Link>
          ) : unread ? (
            <button
              type="button"
              onClick={() => markIds([n.id])}
              className="touch-target inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 bg-surface text-sm font-medium text-slate-700 hover:bg-slate-50"
              aria-label={`Mark read: ${title}`}
            >
              <FaCheck size={10} aria-hidden="true" /> Read
            </button>
          ) : null}
        </div>
      </li>
    );
  };

  const listBar = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-3 sm:px-4 py-2.5 border-b border-slate-100">
      <input
        type="checkbox"
        className="w-4 h-4 accent-primary-600 [@media(pointer:coarse)]:w-5 [@media(pointer:coarse)]:h-5"
        checked={allSelected}
        onChange={toggleAll}
        disabled={rows.length === 0}
        aria-label="Select every notification shown"
      />
      <h2 ref={listHeadingRef} tabIndex={-1} className="text-sm font-semibold text-slate-800 focus:outline-none">
        {loading && !facets ? 'Notifications' : plural(total, 'notification')}
        {facets && facets.status.unread > 0 && filters.status !== 'read' && (
          <span className="font-normal text-slate-500"> · {facets.status.unread} unread</span>
        )}
      </h2>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {selectedCount > 0 && (
          <button
            type="button"
            onClick={markSelected}
            className="touch-target inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-sm font-medium text-white hover:bg-primary-700"
          >
            <FaCheck size={11} aria-hidden="true" /> Mark {selectedCount} selected read
          </button>
        )}
        <button
          type="button"
          onClick={markAll}
          disabled={busy || unreadMatching === 0}
          className="touch-target inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-surface text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {filtered ? `Mark all ${unreadMatching} shown read` : 'Mark all read'}
        </button>
      </div>
    </div>
  );

  const pendingPill =
    pending.length > 0 ? (
      <div className="sticky top-20 z-10 flex justify-center pointer-events-none -mb-2">
        <button
          type="button"
          onClick={showPending}
          className="pointer-events-auto touch-target inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-600 text-sm font-semibold text-white shadow-lg hover:bg-primary-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300"
        >
          <FaArrowUp size={11} aria-hidden="true" /> {pending.length} new — show
        </button>
      </div>
    ) : null;

  const listBody =
    loading && rows.length === 0 ? (
      <LoadingState label="Loading your notifications..." />
    ) : rows.length === 0 ? (
      filtered ? (
        <EmptyState icon={FaBell} title="No notifications match these filters">
          <button type="button" onClick={clearAll} className="mt-2 text-primary-700 font-medium hover:underline">
            Clear the filters
          </button>
        </EmptyState>
      ) : (
        <EmptyState icon={FaBell} title="Nothing yet">
          Alerts from your LexiStar notification rules appear here as they come in.
        </EmptyState>
      )
    ) : (
      <ul className={phone ? 'space-y-3' : 'divide-y divide-slate-100'} aria-label="Notifications" aria-busy={loading}>
        {rows.map(row)}
      </ul>
    );

  const loadMoreButton = nextCursor ? (
    <div className="flex justify-center py-4">
      <button
        type="button"
        onClick={loadMore}
        disabled={loadingMore}
        className="touch-target inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 bg-surface text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
      >
        {loadingMore ? 'Loading…' : 'Load more'}
      </button>
    </div>
  ) : rows.length > 0 && !loading ? (
    <p className="py-4 text-center text-xs text-slate-500">That is everything{filtered ? ' these filters match' : ''}.</p>
  ) : null;

  if (phone) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50" data-phone-layout="">
        {header}
        {liveRegions}
        <div className="flex-1 pt-4 pb-20 space-y-3">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          {searchField}
          <div className="grid grid-cols-1 gap-2">
            {phoneFilter}
            {dateField}
          </div>
          {filtered && (
            <div className="flex items-center justify-between px-1 text-sm text-slate-600">
              <span>{plural(activeFilterCount(filters), 'filter')} on</span>
              <button type="button" onClick={clearAll} className="touch-target px-3 font-medium text-primary-700">
                Clear all
              </button>
            </div>
          )}
          <div ref={topRef} aria-hidden="true" />
          {pendingPill}
          <section aria-label="Your notifications" className={cardClasses}>
            {listBar}
          </section>
          {listBody}
          {loadMoreButton}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      {liveRegions}
      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />

        <section aria-label="Filters" className={`${cardClasses} p-3 sm:p-4 mb-4 space-y-3`}>
          <div className="flex flex-wrap items-end gap-3">
            {searchField}
            {selects}
            {dateField}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {severityChips}
            {filtered && (
              <button
                type="button"
                onClick={clearAll}
                className="ml-auto inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
              >
                <FaTimes size={10} aria-hidden="true" /> Clear all filters
              </button>
            )}
          </div>
        </section>

        <div ref={topRef} aria-hidden="true" />
        {pendingPill}
        <section aria-label="Your notifications" className={`${cardClasses} overflow-hidden`}>
          {listBar}
          {listBody}
          {loadMoreButton}
        </section>
      </div>
    </div>
  );
}
